import React, {useCallback, useContext, useEffect, useState} from 'react';
import {AuthContext} from "../auth/AuthContext";
import {createFetchRequest} from "../util/fetcher";
import {retrieveErrorMessage} from "../util/utilites";
import {BusyContainer} from "./BusyContainer";
import {FullArticle, PriceLine, Stone} from "../models";
import {createPortal} from "react-dom";
import {ShowImageUpload} from "./ShowImageUpload";
import {useApiState} from "../util/useApiState";

interface ArticleProps {
    id: number;
}

interface LoadState {
    loading: boolean;
    error?: string;
    article?: FullArticle;
}

interface ImageViewerProps {
    pictures: string[];
    onClose: (updated: boolean) => void;
}

const ImageViewer: React.FunctionComponent<ImageViewerProps> = ({pictures, onClose}) => {
    const [curPic, setCurPic] = useState(0);
    const {sessionId} = useContext(AuthContext);
    const [loading, roles, loadRoles, setRoles] = useApiState<string[]>([]);

    const nextPic = (e?: React.SyntheticEvent) => {
        e?.stopPropagation();
        if (curPic+1 < pictures.length) {
            setCurPic(curPic+1);
            return;
        }
        setCurPic(0);
    }

    const previousPic = (e?: React.SyntheticEvent) => {
        e?.stopPropagation();
        if (curPic-1 >= 0) {
            setCurPic(curPic-1)
            return;
        }
        setCurPic(pictures.length-1);
    }

    const suggestCloseMessage = () => {
        onClose(false);
    }

    const handleKeyDown = useCallback((e: KeyboardEvent): void => {
        switch (e.key) {
            case 'ArrowLeft':
                previousPic();
                break;
            case 'ArrowRight':
                nextPic();
                break;
            case 'Left':
                previousPic();
                break;
            case 'Right':
                nextPic();
                break;
            case 'Escape':
                suggestCloseMessage();
                break;
        }
    }, []);

    const handleCloseEvent = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (e.currentTarget.tagName?.toLocaleLowerCase() !== 'div') {
            return;
        }
        onClose(false);
    }

    const deletePicture = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        // eslint-disable-next-line no-restricted-globals
        if (confirm('Are you sure you want to delete this picture?')) {
            void createFetchRequest(`api/media/delete/${pictures[curPic].replace('./', '')}`, sessionId )
                .then(resp => resp.ok && onClose(true) )
                .catch( () => {});
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown );
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [curPic, handleKeyDown]);

    useEffect(() => {
        if (loading || roles?.length) {
            return;
        }
        void loadRoles().then( () => createFetchRequest('api/user/permissions', sessionId)
            .then(resp => resp.json())
            .then( r => setRoles(r))
            .catch( () => setRoles(["None"])) );
    }, [roles, sessionId, loading, loadRoles, setRoles])

    return createPortal(<>
        <div id="container" onClick={handleCloseEvent}>
            <div className="navigate-left" onClick={previousPic}><span className="tool-button">⏪</span></div>
            <img src={pictures[curPic]} alt={pictures[curPic]} onKeyDown={e => { handleKeyDown(e.nativeEvent)}} />
            <div className="navigate-right" onClick={nextPic}><span className="tool-button">⏩</span></div>
            { roles && roles.includes('Admin') && <div><button type="button" className="tool-button danger" onClick={deletePicture}>🗑 delete</button></div>}
        </div>
    </>, document.body);
}

export const ShowArticle: React.FunctionComponent<ArticleProps> = ({ id }) => {
    const {sessionId} = useContext(AuthContext);
    const [data, setData] = useState<LoadState>({
        loading: false
    });
    const [showGallery, setShowGallery] = useState(false);

    useEffect(() => {
        if (data.loading) {
            return;
        }
        if (data.error) {
            return;
        }
        if (data.article) {
            return;
        }
        void createFetchRequest(`api/price/itemprice/${id}/0`, sessionId)
            .then( res => res.json())
            .then( (fullArticle: FullArticle) => { setData({
                loading: false,
                error: undefined,
                article: fullArticle
            }); })
            .catch( (e: unknown) => { setData({ ...data, error: retrieveErrorMessage(e) }); });
    }, [data, id, sessionId]);

    const renderGallery = (): void => {
        setShowGallery(true);
    };

    const closeGallery = (updated: boolean): void => {
        setShowGallery(false);
        if (updated) {
            reloadItem();
        }
    };

    const reloadItem = (): void => {
        setData({
            loading: false,
            error: undefined,
            article: undefined
        });
    }

    return (
        <>
            <div className="article">
                { data.loading && <BusyContainer text="laden" /> }
                { data.error && <div className="error">{ data.error }</div> }
                { data.article && <>
                    { data.article.Stones.length > 0 && <>
                        <div className="label">Stenen</div>
                        <div className="stones">
                            <div className="header">
                                <div className="col-xs-4">type</div>
                                <div className="col-xs-4">code</div>
                                <div className="col-xs-4">aantal</div>
                            </div>
                            { data.article.Stones.map( (s: Stone) => (
                                <div className="row">
                                    <div className="col-xs-4">{ s.ModelName }</div>
                                    <div className="col-xs-4">{ s.StoneCode }</div>
                                    <div className="col-xs-4">{ s.Unit }</div>
                                </div>
                            ))}
                        </div>
                    </> }
                    <div className="label">Prijs informatie</div>
                    <div className="pricelines">
                        { data.article.PriceLines.length > 0 && <>
                            <div className="header">
                                <div className="col-xs-3">Prijs berekening</div>
                                <div className="col-xs-3">VS</div>
                                <div className="col-xs-3">SI</div>
                                <div className="col-xs-3">CZ</div>
                            </div>
                            { data.article.PriceLines.map( (pl: PriceLine) => (
                                <div className="row">
                                    <div className="col-xs-3">{ pl.Name }</div>
                                    <div className="col-xs-3">{ pl.Vs } &euro;</div>
                                    <div className="col-xs-3">{ pl.Si } &euro;</div>
                                    <div className="col-xs-3">{ pl.Cz } &euro;</div>
                                </div>
                            ))}
                        </>}
                    </div>
                    <div className="label">Foto's</div>
                    <div className="gallery">
                        { data.article.Pictures.map( (picture: string) =>(
                            <div key={picture} className="picture"><img alt={picture} src={picture} onClick={renderGallery} /></div>
                        ) ) }
                        { showGallery && <ImageViewer pictures={data.article.Pictures} onClose={closeGallery} /> }
                        <ShowImageUpload article={data.article.Code} onUpload={reloadItem} />
                    </div>
                </> }
            </div>
        </>
    );
}
